/**
 * "Pick your helpers": shown once, right after a new player is made, so the
 * helper settings are found on day one instead of only by exploring
 * Settings (docs/WORDS_AND_SENTENCES.md, part 3).
 *
 * Each helper is a big card: a picture, its name, the same one-line
 * description Settings uses, and an On/Off pill. A click or Enter flips it.
 * The tall stage leaves out the helpers a phone never shows (its own
 * keyboard replaces the hints, and there's no room for the word list).
 */
import Phaser from 'phaser';
import { drawVerticalGradient, MENU_GRADIENT } from '../art/backdrop';
import { HELPERS, helperOn } from '../data/helpers';
import type { Helper, HelperIcon } from '../data/helpers';
import { audioOf } from '../systems/audioRegistry';
import { setDevState } from '../systems/devQuery';
import type { SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { pressButton, styleButton } from './ui/menuButton';

const CARD_W = IS_PORTRAIT ? GAME_WIDTH - 48 : 560;
const CARD_H = IS_PORTRAIT ? 150 : 132;
const COLUMN_GAP = 24;
const ROW_GAP = IS_PORTRAIT ? 20 : 16;
const GRID_TOP = IS_PORTRAIT ? 300 : 170;

interface Card {
  readonly helper: Helper;
  readonly frame: Phaser.GameObjects.Graphics;
  readonly pill: Phaser.GameObjects.Text;
  readonly x: number;
  readonly y: number;
}

export class Helpers extends Phaser.Scene {
  private save!: SaveManager;
  private cards: Card[] = [];
  private start!: Phaser.GameObjects.Text;
  /** Cards first, then the Start button. */
  private focused = 0;

  constructor() {
    super('Helpers');
  }

  create(): void {
    setDevState({ scene: 'Helpers' });
    this.save = this.registry.get('save') as SaveManager;
    this.save.markHelpersSeen();
    this.cards = [];
    this.focused = 0;
    drawVerticalGradient(this, MENU_GRADIENT);

    const name = this.save.activeProfile()?.name ?? 'climber';
    this.add
      .text(GAME_WIDTH / 2, IS_PORTRAIT ? 120 : 60, `Pick your helpers, ${name}`, {
        fontFamily: FONT_FAMILY,
        fontSize: IS_PORTRAIT ? '44px' : '46px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 6,
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 60 },
      })
      .setOrigin(0.5);
    this.add
      .text(
        GAME_WIDTH / 2,
        IS_PORTRAIT ? 210 : 112,
        'Turn on anything that helps. You can change these any time in Settings.',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '22px',
          color: PALETTE.ice,
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 80 },
        },
      )
      .setOrigin(0.5);

    const shown = HELPERS.filter((helper) => !IS_PORTRAIT || !helper.wideOnly);
    shown.forEach((helper, index) => this.addCard(helper, index));

    const lastRow = IS_PORTRAIT ? shown.length - 1 : Math.ceil(shown.length / 2) - 1;
    const startY = GRID_TOP + (lastRow + 1) * (CARD_H + ROW_GAP) + (IS_PORTRAIT ? 70 : 40);
    this.start = this.add
      .text(GAME_WIDTH / 2, Math.min(startY, GAME_HEIGHT - 60), 'Start climbing', {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: PALETTE.snow,
        backgroundColor: '#1b2a41',
        padding: { x: 28, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.start.on('pointerover', () => this.focus(this.cards.length));
    this.start.on('pointerup', () => pressButton(this.start, () => this.finish()));

    this.installInput();
    this.focus(0);
  }

  private addCard(helper: Helper, index: number): void {
    const column = IS_PORTRAIT ? 0 : index % 2;
    const row = IS_PORTRAIT ? index : Math.floor(index / 2);
    const x = IS_PORTRAIT
      ? GAME_WIDTH / 2
      : GAME_WIDTH / 2 + (column - 0.5) * (CARD_W + COLUMN_GAP);
    const y = GRID_TOP + row * (CARD_H + ROW_GAP) + CARD_H / 2;
    const left = x - CARD_W / 2;

    const frame = this.add.graphics();
    this.drawIcon(helper.icon, left + 56, y);
    this.add.text(left + 108, y - CARD_H / 2 + 22, helper.label, {
      fontFamily: FONT_FAMILY,
      fontSize: '28px',
      fontStyle: 'bold',
      color: PALETTE.snow,
    });
    this.add.text(left + 108, y - CARD_H / 2 + 62, helper.help, {
      fontFamily: FONT_FAMILY,
      fontSize: IS_PORTRAIT ? '21px' : '19px',
      color: PALETTE.ice,
      wordWrap: { width: CARD_W - 130 },
      lineSpacing: 4,
    });
    const pill = this.add
      .text(x + CARD_W / 2 - 20, y - CARD_H / 2 + 22, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        fontStyle: 'bold',
        padding: { x: 14, y: 4 },
      })
      .setOrigin(1, 0);

    const hit = this.add
      .rectangle(x, y, CARD_W, CARD_H, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    const card: Card = { helper, frame, pill, x, y };
    hit.on('pointerover', () => this.focus(index));
    hit.on('pointerup', () => this.flip(card));
    this.cards.push(card);
    this.drawCard(card, false);
  }

  private drawCard(card: Card, focused: boolean): void {
    const on = helperOn(card.helper, this.save.settings());
    const { frame, x, y } = card;
    frame.clear();
    frame.fillStyle(on ? 0x1d3a4f : 0x16233a, 1);
    frame.fillRoundedRect(x - CARD_W / 2, y - CARD_H / 2, CARD_W, CARD_H, 20);
    frame.lineStyle(focused ? 5 : 3, focused ? 0xffc145 : on ? 0x2ec4b6 : 0x3b4a63, 1);
    frame.strokeRoundedRect(x - CARD_W / 2, y - CARD_H / 2, CARD_W, CARD_H, 20);
    card.pill
      .setText(on ? 'On' : 'Off')
      .setColor(on ? PALETTE.ink : PALETTE.snow)
      .setBackgroundColor(on ? '#2ec4b6' : '#3b4a63');
  }

  private flip(card: Card): void {
    const { helper } = card;
    const settings = this.save.settings();
    this.save.setSetting(helper.key, !settings[helper.key]);
    const next = this.save.settings();
    if (helper.key === 'reducedMotion') this.registry.set('reducedMotion', next.reducedMotion);
    this.sound.mute = next.sfxMuted && next.musicMuted;
    audioOf(this)?.applySettings(next);
    audioOf(this)?.play('key');
    this.focus(this.cards.indexOf(card));
  }

  private finish(): void {
    this.scene.start('LevelMap');
  }

  private focus(index: number): void {
    this.focused = index;
    this.cards.forEach((card, i) => this.drawCard(card, i === index));
    styleButton(this.start, index === this.cards.length);
  }

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const count = this.cards.length + 1;
      if (event.key === 'Escape') {
        this.finish();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        this.focus((this.focused + count - 1) % count);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        this.focus((this.focused + 1) % count);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const card = this.cards[this.focused];
        if (card !== undefined) this.flip(card);
        else pressButton(this.start, () => this.finish());
      }
    });
  }

  /** A small picture of what each helper does, drawn rather than loaded. */
  private drawIcon(icon: HelperIcon, cx: number, cy: number): void {
    const g = this.add.graphics();
    g.fillStyle(0x0b1220, 0.6);
    g.fillCircle(cx, cy, 38);
    g.lineStyle(3, 0x8ecae6, 1);
    g.fillStyle(0xf7fbff, 1);
    switch (icon) {
      case 'keyboard':
        g.strokeRoundedRect(cx - 26, cy - 16, 52, 32, 6);
        for (let r = 0; r < 2; r += 1) {
          for (let c = 0; c < 4; c += 1) {
            g.fillStyle(r === 0 && c === 2 ? 0xffc145 : 0xf7fbff, 1);
            g.fillRect(cx - 20 + c * 11, cy - 10 + r * 11, 7, 7);
          }
        }
        break;
      case 'list':
        for (let i = 0; i < 4; i += 1) {
          g.fillStyle(i === 1 ? 0xffc145 : 0xf7fbff, 1);
          g.fillCircle(cx - 18, cy - 15 + i * 10, 3);
          g.fillRect(cx - 10, cy - 17 + i * 10, i === 1 ? 30 : 24, 4);
        }
        break;
      case 'enter':
        g.lineStyle(5, 0xf7fbff, 1);
        g.beginPath();
        g.moveTo(cx + 16, cy - 16);
        g.lineTo(cx + 16, cy + 4);
        g.lineTo(cx - 12, cy + 4);
        g.strokePath();
        g.fillTriangle(cx - 20, cy + 4, cx - 8, cy - 6, cx - 8, cy + 14);
        break;
      case 'music':
        g.fillCircle(cx - 10, cy + 12, 7);
        g.fillCircle(cx + 12, cy + 7, 7);
        g.fillRect(cx - 5, cy - 16, 4, 28);
        g.fillRect(cx + 17, cy - 21, 4, 28);
        g.fillRect(cx - 5, cy - 21, 26, 6);
        break;
      case 'sound':
        g.fillRect(cx - 22, cy - 7, 10, 14);
        g.fillTriangle(cx - 12, cy - 7, cx + 2, cy - 18, cx + 2, cy + 18);
        g.fillTriangle(cx - 12, cy + 7, cx + 2, cy - 18, cx + 2, cy + 18);
        g.beginPath();
        g.arc(cx + 4, cy, 12, -0.8, 0.8);
        g.strokePath();
        g.beginPath();
        g.arc(cx + 4, cy, 20, -0.8, 0.8);
        g.strokePath();
        break;
      case 'calm':
        // A crescent moon: calm and still.
        g.fillCircle(cx, cy, 18);
        g.fillStyle(0x16233a, 1);
        g.fillCircle(cx + 9, cy - 6, 15);
        break;
    }
  }
}
